import React from 'react';
import { WIDGET_ICON_MAP } from '../../../widgetIconMap';
import LeyningDisplay from '../../../shared/LeyningDisplay';

const timeToMinutes = (s) => {
    if (!s || s === '--:--') return -1;
    const [h, m] = s.split(':').map(Number);
    return h * 60 + m;
};

const StateScreen = ({ msg, isError }) => (
    <div style={{
        width: '100%', height: '100%',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        backgroundColor: '#f8f9fb', direction: 'rtl',
    }}>
        <span style={{ fontSize: '1.125rem', color: isError ? '#c0392b' : '#94a3b8' }}>{msg}</span>
    </div>
);

const DayGrid = ({
    moadData, processedCustomTimes, loading, error,
    title = 'מועד', showTitle = true, days = [],
    entryIcon = 'Sun', exitIcon = 'Star', dayLabels = [],
    drashotByDay = {},
    showLeyning, showHaftarah, leyningDisplayMode,
}) => {
    const DefaultEntryIcon = WIDGET_ICON_MAP[entryIcon] || WIDGET_ICON_MAP['Sun'];
    const DefaultExitIcon  = WIDGET_ICON_MAP[exitIcon]  || WIDGET_ICON_MAP['Star'];

    if (loading) return <StateScreen msg="טוען זמני מועד..." />;
    if (error)   return <StateScreen msg={error} isError />;
    if (!moadData?.days?.length) return <StateScreen msg="אין נתוני מועד" />;

    const timesByDay = {};
    (processedCustomTimes || []).forEach(ct => {
        const idx = ct.dayIndex ?? 0;
        if (!timesByDay[idx]) timesByDay[idx] = [];
        timesByDay[idx].push(ct);
    });
    Object.values(timesByDay).forEach(g =>
        g.sort((a, b) => (a._sortKey ?? timeToMinutes(a.time)) - (b._sortKey ?? timeToMinutes(b.time)))
    );

    const isSingleDay = moadData.days.length === 1;

    return (
        <div style={{
            width: '100%', height: '100%',
            display: 'flex', flexDirection: 'column',
            direction: 'rtl', boxSizing: 'border-box',
            backgroundColor: '#f8f9fb',
            overflow: 'hidden',
        }}>
            {/* Header */}
            {showTitle && (
                <div style={{
                    padding: '16px 20px',
                    borderBottom: '1px solid #e2e8f0',
                    flexShrink: 0,
                    backgroundColor: '#ffffff',
                }}>
                    <span style={{
                        fontSize: '1.25rem', fontWeight: 700,
                        color: '#1e3a5f', letterSpacing: '0.01em',
                    }}>
                        {title}
                    </span>
                </div>
            )}

            {/* Grid of day cards */}
            <div style={{
                flex: 1,
                overflowY: 'auto',
                padding: 12,
                display: 'grid',
                gridTemplateColumns: isSingleDay ? '1fr' : 'repeat(auto-fill, minmax(200px, 1fr))',
                gridAutoRows: 'max-content',
                gap: 10,
                alignContent: 'start',
            }}>
                {moadData.days.map((day, dayIdx) => {
                    const dayColor = days[dayIdx]?.color || '#166534';
                    const dayTimes = timesByDay[dayIdx] || [];
                    const DayEntryIcon = WIDGET_ICON_MAP[dayLabels[dayIdx]?.entryIcon] || DefaultEntryIcon;
                    const DayExitIcon  = WIDGET_ICON_MAP[dayLabels[dayIdx]?.exitIcon]  || DefaultExitIcon;

                    return (
                        <div key={dayIdx} style={{
                            backgroundColor: '#ffffff',
                            borderRadius: 12,
                            overflow: 'hidden',
                            boxShadow: '0 1px 4px rgba(0,0,0,0.07)',
                            display: 'flex', flexDirection: 'column',
                        }}>
                            {/* Card header — day name */}
                            <div style={{
                                padding: '10px 14px',
                                backgroundColor: dayColor,
                                display: 'flex', alignItems: 'center',
                            }}>
                                <span style={{
                                    fontSize: '0.95rem', fontWeight: 700,
                                    color: '#ffffff', letterSpacing: '0.01em',
                                }}>
                                    {day.label}
                                </span>
                            </div>

                            {/* Entry / Exit times */}
                            {(day.entryTime !== null || day.exitTime !== null) && (
                                <div style={{
                                    display: 'grid',
                                    gridTemplateColumns: (day.entryTime !== null && day.exitTime !== null) ? '1fr 1fr' : '1fr',
                                    borderBottom: dayTimes.length > 0 ? '1px solid #f1f5f9' : 'none',
                                }}>
                                    {/* Entry */}
                                    {day.entryTime !== null && (
                                        <div style={{
                                            padding: '12px 14px',
                                            borderLeft: day.exitTime !== null ? '1px solid #f1f5f9' : 'none',
                                            display: 'flex', flexDirection: 'column', gap: 2,
                                        }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                                                <DayEntryIcon style={{ width: 12, height: 12, color: '#166534' }} />
                                                <span style={{ fontSize: '0.65rem', fontWeight: 600, color: '#94a3b8', letterSpacing: '0.06em' }}>
                                                    {dayLabels[dayIdx]?.entry || 'זמן א\''}
                                                </span>
                                            </div>
                                            <span style={{ fontSize: isSingleDay ? '2.25rem' : '1.625rem', fontWeight: 800, color: '#166534', fontVariantNumeric: 'tabular-nums', direction: 'ltr', lineHeight: 1.1 }}>{day.entryTime}</span>
                                        </div>
                                    )}

                                    {/* Exit */}
                                    {day.exitTime !== null && (
                                        <div style={{
                                            padding: '12px 14px',
                                            display: 'flex', flexDirection: 'column', gap: 2,
                                        }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                                                <DayExitIcon style={{ width: 12, height: 12, color: '#1e3a5f' }} />
                                                <span style={{ fontSize: '0.65rem', fontWeight: 600, color: '#94a3b8', letterSpacing: '0.06em' }}>
                                                    {dayLabels[dayIdx]?.exit || 'זמן ב\''}
                                                </span>
                                            </div>
                                            <span style={{ fontSize: isSingleDay ? '2.25rem' : '1.625rem', fontWeight: 800, color: '#1e3a5f', fontVariantNumeric: 'tabular-nums', direction: 'ltr', lineHeight: 1.1 }}>{day.exitTime}</span>
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* דרשה */}
                            {drashotByDay[dayIdx] && (
                                <div style={{
                                    padding: '8px 14px',
                                    borderBottom: (dayTimes.length > 0 || ((showLeyning || showHaftarah) && day.leyning)) ? '1px solid #f1f5f9' : 'none',
                                }}>
                                    <div style={{
                                        fontSize: '0.85rem', fontWeight: 600,
                                        color: '#1e3a5f', fontStyle: 'italic',
                                        whiteSpace: 'pre-wrap', lineHeight: 1.4,
                                    }}>
                                        {drashotByDay[dayIdx]}
                                    </div>
                                </div>
                            )}

                            {/* Leyning */}
                            {(showLeyning || showHaftarah) && day.leyning && (
                                <div style={{
                                    padding: '8px 14px',
                                    borderBottom: dayTimes.length > 0 ? '1px solid #f1f5f9' : 'none',
                                }}>
                                    <LeyningDisplay
                                        leyning={day.leyning}
                                        mode={leyningDisplayMode}
                                        showHaftarah={showHaftarah}
                                        showMaftir={leyningDisplayMode === 'full'}
                                        compact={true}
                                        style={{ padding: '8px 0' }}
                                    />
                                </div>
                            )}

                            {/* Custom times for this day */}
                            {dayTimes.length > 0 && (
                                <div style={{ padding: '8px 14px 12px' }}>
                                    {dayTimes.map((ct, idx) => (
                                        <div key={idx} style={{
                                            display: 'flex', alignItems: 'center',
                                            justifyContent: 'space-between',
                                            padding: '5px 0',
                                            borderBottom: idx < dayTimes.length - 1 ? '1px solid #f8fafc' : 'none',
                                        }}>
                                            <span style={{ fontSize: '0.8rem', color: '#64748b', fontWeight: 500 }}>
                                                {ct.label}
                                            </span>
                                            <span style={{
                                                fontSize: '0.925rem', fontWeight: 700,
                                                color: '#1e3a5f',
                                                fontVariantNumeric: 'tabular-nums', direction: 'ltr',
                                            }}>
                                                {ct.time}
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export default DayGrid;
